import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import './NotificationPopup.scss';
const NotificationPopupContext = React.createContext({
    showNotification: () => '',
    dismissNotification: () => { },
});
const MAX_VISIBLE_NOTIFICATIONS = 3;
const DEFAULT_DISMISS_MS = 5000;
const EXIT_ANIMATION_MS = 200;
const getTypeIcon = (type) => {
    switch (type) {
        case 'success':
            return 'pi pi-check';
        case 'warning':
            return 'pi pi-exclamation-triangle';
        case 'error':
            return 'pi pi-times-circle';
        default:
            return 'pi pi-info-circle';
    }
};
const NotificationPopupCard = ({ notification, onRemove, }) => {
    var _a, _b, _c, _d, _e, _f;
    const [isExiting, setIsExiting] = useState(false);
    const timerRef = useRef(null);
    const startedAtRef = useRef(0);
    const remainingRef = useRef((_a = notification.duration) !== null && _a !== void 0 ? _a : DEFAULT_DISMISS_MS);
    const dismissingRef = useRef(false);
    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);
    const finalizeDismiss = useCallback(() => {
        if (dismissingRef.current) {
            return;
        }
        dismissingRef.current = true;
        clearTimer();
        setIsExiting(true);
        window.setTimeout(() => {
            var _a;
            (_a = notification.onDismiss) === null || _a === void 0 ? void 0 : _a.call(notification);
            onRemove(notification.id);
        }, EXIT_ANIMATION_MS);
    }, [clearTimer, notification, onRemove]);
    const startTimer = useCallback(() => {
        clearTimer();
        startedAtRef.current = Date.now();
        timerRef.current = setTimeout(finalizeDismiss, remainingRef.current);
    }, [clearTimer, finalizeDismiss]);
    useEffect(() => {
        startTimer();
        return clearTimer;
    }, [clearTimer, startTimer]);
    const handleMouseEnter = () => {
        if (dismissingRef.current) {
            return;
        }
        const elapsed = Date.now() - startedAtRef.current;
        remainingRef.current = Math.max(0, remainingRef.current - elapsed);
        clearTimer();
    };
    const handleMouseLeave = () => {
        if (dismissingRef.current || remainingRef.current <= 0) {
            return;
        }
        startTimer();
    };
    const handleView = () => {
        var _a;
        (_a = notification.onView) === null || _a === void 0 ? void 0 : _a.call(notification);
        finalizeDismiss();
    };
    return (React.createElement("div", { className: `notification-popup-card type-${(_b = notification.type) !== null && _b !== void 0 ? _b : 'info'} ${isExiting ? 'is-exiting' : ''}`, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave },
        React.createElement("div", { className: "notification-popup-icon" },
            React.createElement("i", { className: getTypeIcon((_c = notification.type) !== null && _c !== void 0 ? _c : 'info') })),
        React.createElement("div", { className: "notification-popup-content" },
            React.createElement("button", { type: "button", className: "notification-popup-close", "aria-label": "Dismiss notification", onClick: finalizeDismiss },
                React.createElement("i", { className: "pi pi-times" })),
            React.createElement("div", { className: "notification-popup-title" }, notification.title),
            React.createElement("div", { className: "notification-popup-message" }, notification.message),
            !!((_d = notification.metadata) === null || _d === void 0 ? void 0 : _d.length) && (React.createElement("div", { className: "notification-popup-metadata" }, notification.metadata.map((item) => (React.createElement("div", { key: `${notification.id}-${item.label}-${item.value}`, className: "notification-popup-metadata-item" },
                React.createElement("span", { className: "notification-popup-metadata-label" },
                    item.label,
                    ":"),
                React.createElement("span", null, item.value)))))),
            React.createElement("div", { className: "notification-popup-actions" },
                !!notification.onView && (React.createElement("button", { type: "button", className: "notification-popup-action notification-popup-action-primary", onClick: handleView }, (_e = notification.viewLabel) !== null && _e !== void 0 ? _e : 'View')),
                React.createElement("button", { type: "button", className: "notification-popup-action notification-popup-action-secondary", onClick: finalizeDismiss }, (_f = notification.dismissLabel) !== null && _f !== void 0 ? _f : 'Dismiss')))));
};
const NotificationPopupProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const dismissNotification = useCallback((id) => {
        setNotifications((prev) => prev.filter((item) => item.id !== id));
    }, []);
    const showNotification = useCallback((options) => {
        const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        setNotifications((prev) => {
            var _a, _b, _c;
            return [
                Object.assign({ id, type: (_a = options.type) !== null && _a !== void 0 ? _a : 'info', duration: (_b = options.duration) !== null && _b !== void 0 ? _b : DEFAULT_DISMISS_MS, dismissLabel: (_c = options.dismissLabel) !== null && _c !== void 0 ? _c : 'Dismiss' }, options),
                ...prev,
            ];
        });
        return id;
    }, []);
    const visibleNotifications = notifications.slice(0, MAX_VISIBLE_NOTIFICATIONS);
    const value = useMemo(() => ({
        showNotification,
        dismissNotification,
    }), [dismissNotification, showNotification]);
    return (React.createElement(NotificationPopupContext.Provider, { value: value },
        children,
        React.createElement("div", { className: "notification-popup-stack" }, visibleNotifications.map((notification) => (React.createElement(NotificationPopupCard, { key: notification.id, notification: notification, onRemove: dismissNotification }))))));
};
export const useNotificationPopup = () => useContext(NotificationPopupContext);
export default NotificationPopupProvider;
